import { effect, inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { BalanceData } from "../types/balance-data.type";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { ApiService } from "./api.service";
import { LiveUpdateService } from "./live-update.service";
import { BetPlacementService } from "./bet-placement.service";
import { ExposureService } from "./exposure.service";
import { StorageService } from "./storage.service";
import { Bet } from "../types/bet.type";
import { downloadJSONAsCSV } from "../utils/common";
import moment from "moment";

interface TransferResponse {
  status: string;
  result: string[];
}

@Injectable({
  providedIn: "root",
})
export class BalanceService implements OnDestroy {
  subscriptions: Subscription = new Subscription();
  http = inject(HttpClient);
  apiService = inject(ApiService);
  balanceData$: BehaviorSubject<BalanceData | null> = new BehaviorSubject<BalanceData | null>(null);
  casinoBalance$: BehaviorSubject<BalanceData | null> = new BehaviorSubject<BalanceData | null>(null);
  liveUpdateService = inject(LiveUpdateService);
  placementService = inject(BetPlacementService);
  exposureService = inject(ExposureService);
  storage = inject(StorageService);
  currentBets: Bet[] = [];

  constructor() {
    effect(() => {
      const data = this.liveUpdateService._getData("wallet");
      if (data) {
        console.log("Balance display update ➡️ ", data);
        if (this.placementService.disablePlacement$.getValue()) {
          this.placementService.disablePlacement$.next(false);
        }
        this.handleBalanceUpdate(data);
      }
    });
    this.subscriptions.add(this.placementService.openBetsSubject.subscribe((bets) => (this.currentBets = bets)));
  }

  handleBalanceUpdate(update: any) {
    const walletId = this.apiService.getUserWalletId();
    if (update?.data && update?.data?.id === walletId) {
      // console.log("wallet update balance display", update);
      const updateData: any = {
        ...this.balanceData$.value,
        BALANCE: update?.data?.balance || "0",
        RISK_EXPOSURE: update?.data?.risk_exposure || "0",
        LIABILITY: update?.data?.liability || "0",
        TOTAL_EXPOSURE: update?.data?.total_exposure || "0",
      };
      this.balanceData$.next(updateData);
      this.setUserBalance(updateData);
      this.placementService.setRefresh(true);
      this.exposureService.setBalance = updateData;
    }
  }

  fetchBalance() {
    const app = this.apiService.getUserToken();
    this.http
      .get<{
        status: string;
        result: BalanceData[];
      }>(`${environment.sURL}${environment.endpoints.balance}`, {
        params: { session_id: app?.user?.ID },
      })
      .subscribe((response: any) => {
        if (response?.result?.length > 0) {
          const mainWallet = response.result.find((balance: any) => balance.IS_FALLBACK);

          if (mainWallet) {
            // sessionStorage.setItem("walletId", mainWallet.ID.toString());
            this.storage.setItem("walletId", mainWallet.ID.toString());
            this.balanceData$.next(mainWallet);
            this.setUserBalance(mainWallet);
            this.exposureService.setBalance = mainWallet;
          }

          if (response?.result?.length > 1) {
            const casinoWallet = response.result.find((balance: any) => !balance.IS_FALLBACK);
            this.casinoBalance$.next(casinoWallet);
          } else {
            this.casinoBalance$.next(null);
          }
        }
      });
  }

  refreshExposure() {
    const walletId = this.balanceData$.value?.ID;
    if (!walletId) return;
    this.http.get(`${environment.sURL}${environment.endpoints.refreshExposure}/${walletId}`).subscribe({
      complete: () => {
        this.placementService.disablePlacement$.next(true);
      },
    });
  }

  setUserBalance(balance: any) {
    // sessionStorage.setItem("wallet_balance", JSON.stringify(balance));
    this.storage.setItem("wallet_balance", balance);
  }

  clearUserBalance() {
    // sessionStorage.removeItem("wallet_balance");
    this.storage.removeItem("wallet_balance");
    this.balanceData$.next(null);
  }

  getUserBalance() {
    // const balance = sessionStorage.getItem("wallet_balance");
    const balance = this.storage.getItem<any>("wallet_balance");
    if (balance !== null) return balance;
    return null;
  }

  async downloadBetsAndExposure() {
    const bets = this.currentBets.map((bet) => ({
      bet_id: bet?.betId,
      market_id: bet?.marketId,
      selection_id: bet?.selectionId,
      side: bet?.side,
      price: bet?.fOdds,
      bet_size: bet?.priceSize?.size,
      size_matched: bet?.stakeMatched ?? bet?.priceSize?.size,
    }));
    const balanceData = this.balanceData$.getValue();
    const exposure = balanceData ? +balanceData?.TOTAL_EXPOSURE - +balanceData?.LIABILITY : 0;
    const walletId = balanceData?.ID;
    const dateTime = moment().format("DD-MMM-HH-mm");

    await downloadJSONAsCSV(bets, `${walletId}-${dateTime}`);
    await downloadJSONAsCSV([{ max_exposure: String(Math.abs(exposure)) }], `${walletId}-${dateTime}-result`);
  }

  transferBalance(body: any) {
    return this.http.post<TransferResponse>(`${environment.sURL}${environment.endpoints.balanceTransfer}`, body);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
